import type {
  AdminEvent,
  AdminEventListResponse,
  AdminEventResponse,
  CreateAdminEventRequest,
  EventCategory,
  UpdateAdminEventRequest,
} from '@mds/shared/types/event';

import { api } from '@/lib/api';

/** The event endpoints — see `backend/docs/events.md`. */

/** A type alias, so it carries the index signature the client's `query` needs. */
export type EventQuery = {
  page?: number;
  limit?: number;
  /** `yyyy-mm`, as the month picker spells it. */
  month?: string;
  category?: EventCategory;
  /** Matched across title, short summary and venue label. */
  q?: string;
  sort?: 'eventDate' | 'createdAt' | 'title';
  direction?: 'asc' | 'desc';
};

export const listEvents = (query: EventQuery = {}) =>
  api.get<AdminEventListResponse>('admin/events', { query });

export const getEvent = (eventId: string): Promise<AdminEvent> =>
  api.get<AdminEventResponse>(`admin/events/${eventId}`).then((r) => r.data);

/** Creating publishes it — there is no draft state. */
export const createEvent = (body: CreateAdminEventRequest): Promise<AdminEvent> =>
  api.post<AdminEventResponse>('admin/events', body).then((r) => r.data);

/**
 * PATCH takes only what changed. Sending `mediaFileIds` replaces the whole
 * gallery in one go; leaving it out keeps the gallery as it is.
 */
export const updateEvent = (eventId: string, body: UpdateAdminEventRequest): Promise<AdminEvent> =>
  api.patch<AdminEventResponse>(`admin/events/${eventId}`, body).then((r) => r.data);

/**
 * Takes its cancelled registrations with it. Refused while any registration is not cancelled, or
 * once a payment was recorded — see `isEventHasRegistrations` and `isEventHasPayments`.
 */
export const deleteEvent = (eventId: string): Promise<null> =>
  api.delete<null>(`admin/events/${eventId}`);
